<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { employeesControllerGetPhotoUrlV1 } from '@/api/generated/employees/employees.js';
import type { PhotoUrlDto } from '@/api/generated/models/index.js';

const props = withDefaults(
  defineProps<{
    employeeId: string;
    photoKey?: string | null;
    lastName?: string;
    firstName?: string;
    size?: number;
  }>(),
  { photoKey: null, lastName: '', firstName: '', size: 48 },
);

const url = ref<string | null>(null);

const initials = computed(() => {
  const a = props.lastName.trim()[0] ?? '';
  const b = props.firstName.trim()[0] ?? '';
  const text = (a + b).toUpperCase();
  return text || '?';
});

/** Детерминированный оттенок по ФИО — чтобы плейсхолдеры были разноцветными. */
const hue = computed(() => {
  const seed = `${props.lastName}${props.firstName}`.trim();
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
});

const style = computed(() => {
  const base: Record<string, string> = {
    width: `${props.size}px`,
    height: `${props.size}px`,
    fontSize: `${Math.round(props.size * 0.38)}px`,
  };
  if (!url.value) {
    // Детерминированный градиент по ФИО + белые инициалы (сочнее плоской заливки).
    const h = hue.value;
    base.background = `linear-gradient(135deg, hsl(${h} 58% 56%), hsl(${(h + 24) % 360} 62% 46%))`;
    base.color = '#fff';
  }
  return base;
});

watch(
  () => [props.employeeId, props.photoKey] as const,
  async ([id, key]) => {
    url.value = null;
    if (!key) return;
    try {
      const res = (await employeesControllerGetPhotoUrlV1(id)) as unknown as PhotoUrlDto;
      url.value = res.url;
    } catch {
      url.value = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <span class="avatar" :style="style">
    <img v-if="url" :src="url" :alt="`${lastName} ${firstName}`" class="avatar__img" />
    <span v-else class="avatar__initials">{{ initials }}</span>
  </span>
</template>

<style scoped>
.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: var(--radius-full);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
  overflow: hidden;
  user-select: none;
}
.avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
